/**
 * Auth Store
 *
 * Zustand store for managing authentication state including:
 * - JWT claims and userinfo
 * - Boot sequence initialization
 * - Login/logout/refresh actions
 * - Computed values (isAuthenticated, isAdmin, etc.)
 *
 * Uses the AuthApiClient from the registry to communicate with the BFF.
 *
 * @module stores/useAuthStore
 */

import { create, type StateCreator } from 'zustand';
import type { AuthApiClient } from '../api/AuthApiClient';
import type { Claims, Identity, Userinfo } from '../types';
import { authClient as defaultAuthClient } from '../api/registry';
import {
  activeOrgIdFromIdentity,
  orgNameFromIdentity,
  getUserEmail as getUserEmailUtil,
  getUserDisplayName as getUserDisplayNameUtil,
} from '../utils';
import { isHttpError, isSessionExpiredError } from '../api/http';

// =============================================================================
// Types
// =============================================================================

/**
 * Auth store state shape.
 */
export interface AuthState {
  // State
  claims: Claims | null;
  /**
   * The caller's identity + org context from the tenant-management backend
   * (`GET /api/tenant-management/me`). Source of org id, roles, and the
   * admin/staff flags — under the v2 contract the browser parses no token.
   */
  identity: Identity | null;
  userinfo: Userinfo | null;
  isInitialized: boolean;
  isLoading: boolean;
  error: Error | null;

  // Computed values (derived from state using utility functions)
  isAuthenticated: boolean;
  /**
   * Whether the user may administer members of their **own** org
   * (`platform.user.admin`) — org-scoped member-admin authority, NOT a
   * platform-wide admin. From the backend identity. Named `isUserAdmin` to keep
   * that scope unambiguous.
   */
  isUserAdmin: boolean;
  /**
   * Whether the user is a ModernPath **staff admin** (`platform.staff.admin`,
   * granted only in the vendor org). Computed server-side and read from the
   * backend identity. Gates the org picker and cross-org member management.
   */
  isStaffAdmin: boolean;
  /**
   * Whether the user may manage team members in their **home** org — real ZITADEL
   * user-management authority + platform-project entitlement, resolved server-side
   * (recognises vendor admins and customer `ORG_USER_MANAGER`s that `isUserAdmin`
   * misses). This is the proactive invite-affordance gate for the user's own org.
   * A staff admin acting on ANOTHER org uses the per-org capability from the users
   * store (`canManageTargetOrg`) instead.
   */
  canManageMembers: boolean;
  /**
   * Whether the user holds instance-level authority to **create and delete
   * tenants** (orgs) — `org.create` + `org.delete` + `user.write`, i.e. an
   * `IAM_ORG_MANAGER` / `IAM_OWNER` membership. From the backend identity. Gates
   * the Create-tenant and Delete-tenant affordances; strictly stronger than
   * {@link isStaffAdmin}.
   */
  canManageTenants: boolean;
  /**
   * The user's **active** (home) organization id, from the backend identity, or
   * `null` when it can't be resolved.
   *
   * This is the org id a **normal admin** sends in the mandatory `{orgId}` URL
   * segment of member operations. A staff admin instead targets the org they
   * pick from the org picker; `activeOrgId` remains their own home org.
   */
  activeOrgId: string | null;
  orgName: string;
  userEmail: string;
  userDisplayName: string;

  // Actions
  initialize: () => Promise<void>;
  login: (returnTo: string) => Promise<void>;
  logout: (returnTo: string) => Promise<void>;
  refresh: () => Promise<boolean>;
  clearAuth: () => void;
}

// =============================================================================
// Factory Function
// =============================================================================

/**
 * Creates the auth store state creator with an optional API client override.
 *
 * @param authClient - Optional auth client override (defaults to registry client)
 * @returns Zustand state creator function
 */
export function createAuthStoreCreator(
  authClient: AuthApiClient = defaultAuthClient
): StateCreator<AuthState> {
  // Closure-based idempotency flag for initialize()
  let initialized = false;

  return (set, get) => {
    /**
     * Compute derived values from current state.
     * Called after state updates to keep computed values in sync.
     */
    const computeDerivedValues = (
      claims: Claims | null,
      identity: Identity | null,
    ) => ({
      isAuthenticated: claims !== null,
      // Authorization flags + org context come from the backend identity;
      // identity display fields (email, name) from the ID token's claims.
      isUserAdmin: identity?.isUserAdmin ?? false,
      isStaffAdmin: identity?.isStaffAdmin ?? false,
      canManageMembers: identity?.canManageMembers ?? false,
      canManageTenants: identity?.canManageTenants ?? false,
      activeOrgId: activeOrgIdFromIdentity(identity),
      orgName: orgNameFromIdentity(identity),
      userEmail: getUserEmailUtil(claims),
      userDisplayName: getUserDisplayNameUtil(claims),
    });

    return {
      // Initial state
      claims: null,
      identity: null,
      userinfo: null,
      isInitialized: false,
      isLoading: false,
      error: null,

      // Initial computed values
      isAuthenticated: false,
      isUserAdmin: false,
      isStaffAdmin: false,
      canManageMembers: false,
      canManageTenants: false,
      activeOrgId: null,
      orgName: orgNameFromIdentity(null),
      userEmail: '',
      userDisplayName: '',

      /**
       * Initialize - Boot sequence action.
       *
       * Called once when app mounts. Determines initial auth state by:
       * 1. Calling getClaims() to check authentication
       * 2. If authenticated, fetching userinfo in parallel (non-blocking)
       *
       * Idempotent: subsequent calls return immediately.
       */
      initialize: async () => {
        // Idempotent - only run once
        if (initialized) {
          return;
        }
        initialized = true;

        set({ isLoading: true, error: null });

        try {
          // getClaims is the auth gate: it reads the ID-token claims straight
          // from the BFF session cookie (/oauth-agent/claims), so a 401 here
          // means there is genuinely no session → unauthenticated (show login,
          // no error). getIdentity supplies org context + authorization flags
          // from the tenant-management backend via the /api ext_proc proxy (the
          // v2 contract: no token parsed in the browser).
          //
          // These two signals are NOT interchangeable, so we must NOT collapse
          // them into one Promise.all where any 401 reads as "logged out". A
          // valid session (getClaims OK) but a failing getIdentity is a real
          // backend/proxy fault (e.g. the ext_proc not injecting the Bearer, or
          // an audience/issuer mismatch), NOT a logout. Treating it as a silent
          // logout produces an invisible login loop — the user signs in, /me
          // still 401s, and they bounce back to the sign-in screen with no
          // explanation. So use allSettled and branch on which call failed.
          // (This is why a getIdentity 401 is surfaced here, mirroring how a
          // getIdentity 502 already fails closed with an error.)
          const [claimsResult, identityResult] = await Promise.allSettled([
            authClient.getClaims(),
            authClient.getIdentity(),
          ]);

          // No BFF session → genuinely unauthenticated. Silent, expected.
          if (claimsResult.status === 'rejected') {
            if (
              isHttpError(claimsResult.reason) &&
              claimsResult.reason.status === 401
            ) {
              set({
                claims: null,
                identity: null,
                userinfo: null,
                isInitialized: true,
                isLoading: false,
                error: null,
                ...computeDerivedValues(null, null),
              });
              return;
            }
            // Unexpected agent failure (5xx, network) → surface it below.
            throw claimsResult.reason;
          }

          // Session is valid but the backend identity call failed. Fail closed
          // WITH an error so the user sees something actionable instead of a
          // silent bounce to login. Stay unauthenticated (claims: null) so the
          // AuthGuard routes to the landing page, which renders `error`.
          if (identityResult.status === 'rejected') {
            const reason = identityResult.reason;
            const status = isHttpError(reason) ? reason.status : undefined;
            const suffix = status ? ` (the server returned ${status})` : '';
            set({
              claims: null,
              identity: null,
              userinfo: null,
              isInitialized: true,
              isLoading: false,
              error: new Error(
                `You're signed in, but we couldn't load your account${suffix}. ` +
                  'Please try again — if this keeps happening, contact ModernPath support.',
              ),
              ...computeDerivedValues(null, null),
            });
            return;
          }

          const claims = claimsResult.value;
          const identity = identityResult.value;

          // Success - user is authenticated
          set({
            claims,
            identity,
            isLoading: false,
            ...computeDerivedValues(claims, identity),
          });

          // Fetch userinfo in parallel (non-blocking)
          // Don't await - let it complete asynchronously. Derived values come
          // from claims + identity, so userinfo arriving later only populates
          // profile fields — no recompute needed.
          authClient
            .getUserInfo()
            .then((userinfo) => {
              set({ userinfo });
            })
            .catch((error) => {
              // Log warning but don't fail boot - userinfo is optional
              console.warn('Failed to fetch userinfo:', error);
            });

          // Mark initialized
          set({ isInitialized: true });
        } catch (error) {
          // Unexpected failure (e.g. a non-401 agent error re-thrown above).
          // Store it but still mark initialized so the app can render the error.
          set({
            error: error instanceof Error ? error : new Error(String(error)),
            isInitialized: true,
            isLoading: false,
          });
        }
      },

      /**
       * Login - Initiates login flow.
       *
       * 1. Calls loginStart with returnTo URL
       * 2. Redirects to authorization URL on success
       * 3. On failure, sets error state
       */
      login: async (returnTo: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authClient.loginStart(returnTo);

          // Redirect to authorization URL
          // Page will unload, so no need to reset isLoading
          window.location.href = response.authorizationUrl;
        } catch (error) {
          set({
            error: error instanceof Error ? error : new Error(String(error)),
            isLoading: false,
          });
        }
      },

      /**
       * Logout - Initiates logout flow.
       *
       * 1. Calls logout with returnTo URL
       * 2. Redirects to end-session URL on success
       * 3. On failure, sets error state
       */
      logout: async (returnTo: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authClient.logout(returnTo);

          // Redirect to end-session URL
          // Page will unload, so no need to clear state
          window.location.href = response.endSessionUrl;
        } catch (error) {
          set({
            error: error instanceof Error ? error : new Error(String(error)),
            isLoading: false,
          });
        }
      },

      /**
       * Refresh - Refreshes tokens.
       *
       * @returns true if refresh succeeded, false if session expired
       * @throws Error for transient failures (caller decides how to handle)
       */
      refresh: async () => {
        try {
          await authClient.refresh();
          // Cookies rotated server-side, no state change needed
          return true;
        } catch (error) {
          // SessionExpiredError - clear auth state, return false
          if (isSessionExpiredError(error)) {
            get().clearAuth();
            return false;
          }

          // 401 from HttpError - also means session expired
          if (isHttpError(error) && error.status === 401) {
            get().clearAuth();
            return false;
          }

          // Transient error - throw or return false
          // Spec says "throw or return false (caller decides how to handle)"
          // We'll throw to give caller full control
          throw error;
        }
      },

      /**
       * Clear Auth - Resets auth state.
       *
       * Used when session dies (e.g., refresh returns SessionExpiredError).
       */
      clearAuth: () => {
        set({
          claims: null,
          identity: null,
          userinfo: null,
          ...computeDerivedValues(null, null),
        });
      },
    };
  };
}

// =============================================================================
// Store Factory
// =============================================================================

/**
 * Creates an auth store instance with an optional API client override.
 *
 * @param authClient - Optional auth client for testing
 * @returns Zustand store hook
 *
 * @example
 * ```typescript
 * // For testing with a mock client
 * const mockClient = new MockAuthApiClient({ claimsError: { type: '401' } });
 * const useTestStore = createAuthStore(mockClient);
 * ```
 */
export function createAuthStore(authClient?: AuthApiClient) {
  return create<AuthState>(createAuthStoreCreator(authClient));
}

// =============================================================================
// Default Export
// =============================================================================

/**
 * Default auth store instance using the registry client.
 *
 * Use this for normal application usage:
 * ```typescript
 * import { useAuthStore } from './stores/useAuthStore';
 *
 * function MyComponent() {
 *   const { isAuthenticated, claims, login } = useAuthStore();
 *   // ...
 * }
 * ```
 */
export const useAuthStore = createAuthStore();
